import { createContext, useContext, useEffect, useState } from "react";

/**
 * DensityContext - global compact/comfortable toggle.
 *
 * When compact is on, layout components add the `cocoa-compact` class to the
 * top-level element. Consumers add `compact:` variant Tailwind classes (see the
 * examples in Dashboard.js) to shrink padding and row heights so clinicians
 * can see roughly 20 percent more per screen without any content change.
 *
 * Preference is persisted in localStorage as `cocoa_density` (`compact` or
 * `comfortable`) so it survives reloads.
 */
const DensityCtx = createContext({ compact: false, setCompact: () => {} });

export function DensityProvider({ children }) {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    try {
      setCompact(localStorage.getItem("cocoa_density") === "compact");
    } catch { /* localStorage unavailable */ }
  }, []);
  useEffect(() => {
    try { localStorage.setItem("cocoa_density", compact ? "compact" : "comfortable"); } catch { /* storage full */ }
    const root = document.documentElement;
    if (compact) root.classList.add("cocoa-compact");
    else root.classList.remove("cocoa-compact");
  }, [compact]);
  return (
    <DensityCtx.Provider value={{ compact, setCompact, toggle: () => setCompact((c) => !c) }}>
      {children}
    </DensityCtx.Provider>
  );
}

export const useDensity = () => useContext(DensityCtx);
